import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  fetchWithTimeout,
  isNetworkError,
  getNetworkErrorMessage,
  TimeoutError,
  getTimeoutErrorMessage
} from '../utils/networkError';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState('');

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setError('Invalid or missing reset token. Please request a new password reset link.');
    } else {
      setToken(tokenParam);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate password
    if (!password) {
      setError('Password is required');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/auth/reset-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ token, password })
        },
        15000 // 15 second timeout
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.errors?.[0]?.msg || 'Failed to reset password');
      }

      setSuccess(true);
    } catch (err) {
      console.error('Reset password error:', err);

      if (err instanceof TimeoutError) {
        setError(getTimeoutErrorMessage());
      } else if (isNetworkError(err)) {
        setError(getNetworkErrorMessage());
      } else {
        setError(err.message || 'Failed to reset password. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-5rem)] px-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-display text-4xl italic tracking-tighter leading-tight mb-2">
            Reset Password
          </h1>
          <p className="font-body text-light-text-secondary dark:text-silver/60">
            {success ? 'Your password has been reset!' : 'Enter your new password below.'}
          </p>
        </div>

        {/* Form */}
        <div className="glass border border-light-border dark:border-border rounded-2xl p-8">
          {success ? (
            <div className="text-center">
              <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl text-green-400">
                <p className="font-body">
                  Your password has been successfully reset.
                </p>
                <p className="mt-2 text-sm text-green-400/80">
                  You can now log in with your new password.
                </p>
              </div>

              <Link
                to="/login"
                className="btn-primary inline-block"
              >
                Go to Login
              </Link>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm" role="alert">
                  {error}
                </div>
              )}

              {!token ? (
                <div className="text-center">
                  <Link
                    to="/forgot-password"
                    className="btn-primary inline-block"
                  >
                    Request New Reset Link
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                  {/* New Password */}
                  <div>
                    <label
                      htmlFor="password"
                      className="block font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2"
                    >
                      New Password
                    </label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="input-field"
                      placeholder="Enter new password"
                      disabled={isSubmitting}
                      autoFocus
                      minLength={8}
                    />
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label
                      htmlFor="confirmPassword"
                      className="block font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2"
                    >
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="input-field"
                      placeholder="Confirm new password"
                      disabled={isSubmitting}
                      minLength={8}
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Resetting...' : 'Reset Password'}
                  </button>
                </form>
              )}

              {/* Back to Login */}
              <p className="mt-6 text-center text-light-text-secondary dark:text-silver/60 font-body text-sm">
                Remember your password?{' '}
                <Link
                  to="/login"
                  className="text-light-text dark:text-pure-white hover:underline transition-smooth"
                >
                  Sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
